import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  Content,
  Header,
  HeaderLabel,
  Link,
  Page,
} from '@backstage/core-components';
import { cockpitTokens } from '../home/cockpitTokens';
import {
  RESPONSIBILITY_FIELDS,
  RESPONSIBILITY_FIELD_ORDER,
  RESPONSIBILITY_MAP_AS_OF,
  RESPONSIBILITY_MAP_SOURCE_URL,
  ResponsibilityArea,
  ResponsibilityField,
  areaAnchorId,
  firstName,
  responsibilityAreas,
} from './responsibilityAreas';

/** Chip filter value: a single field, or all of them. */
type FieldFilter = ResponsibilityField | 'Alle';

const useStyles = makeStyles(theme => ({
  intro: {
    color: cockpitTokens.color.textMuted,
    maxWidth: 760,
    marginBottom: theme.spacing(3),
    lineHeight: 1.7,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  chip: {
    background: cockpitTokens.color.surface,
    border: `1px solid ${cockpitTokens.color.border}`,
    color: cockpitTokens.color.textMuted,
    fontWeight: 600,
    '&:hover': {
      borderColor: cockpitTokens.color.borderHover,
      color: cockpitTokens.color.text,
    },
  },
  chipActive: {
    background: cockpitTokens.color.brand,
    borderColor: cockpitTokens.color.brand,
    color: cockpitTokens.color.text,
    '&:hover': {
      background: cockpitTokens.color.brand,
      color: cockpitTokens.color.text,
    },
  },
  /**
   * The area count is metadata, not part of the field name. Two cues carry
   * that, so it still reads correctly if either is lost: parentheses (survive
   * printing and forced-colours mode) and reduced opacity. Tabular figures keep
   * chip widths from jittering.
   *
   * Deliberately not the accent yellow — that colour marks the *active* element
   * (selected sidebar item, "Alle aufklappen"); spending it on every chip's
   * counter would drain it of meaning.
   */
  chipCount: {
    opacity: 0.6,
    marginLeft: theme.spacing(0.6),
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 500,
  },
  expandAll: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    font: 'inherit',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: cockpitTokens.color.brand,
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    padding: theme.spacing(1, 0.5),
    '&:focus-visible': {
      outline: `2px solid ${cockpitTokens.color.brand}`,
      outlineOffset: 2,
      borderRadius: 6,
    },
  },
  count: {
    color: cockpitTokens.color.textFaint,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(2),
  },
  card: {
    background: cockpitTokens.color.surface,
    border: `1px solid ${cockpitTokens.color.border}`,
    // The field's hue as a left spine, mirroring the source board's colour key.
    borderLeft: `3px solid var(--field-color)`,
    borderRadius: cockpitTokens.radius.card,
    boxShadow: cockpitTokens.shadow.card,
    // Deliberately *not* height:100% — a card must size to its own content, or
    // an expanded card stretches its collapsed row neighbour to match and
    // leaves a large void above that card's "Details" toggle.
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      boxShadow: cockpitTokens.shadow.cardHover,
    },
  },
  cardFlash: {
    boxShadow: `0 0 0 3px ${cockpitTokens.color.brand}`,
  },
  head: {
    font: 'inherit',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    padding: theme.spacing(2.5, 3, 0),
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    width: '100%',
    '&:focus-visible': {
      outline: `2px solid ${cockpitTokens.color.brand}`,
      outlineOffset: -2,
      borderRadius: cockpitTokens.radius.card,
    },
  },
  fieldBadge: {
    alignSelf: 'flex-start',
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    padding: theme.spacing(0.4, 1.2),
    borderRadius: cockpitTokens.radius.pill,
    color: 'var(--field-color)',
    border: `1px solid var(--field-color)`,
    marginBottom: theme.spacing(1.5),
  },
  bereich: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: cockpitTokens.color.text,
    letterSpacing: '-0.01em',
    marginBottom: theme.spacing(0.5),
  },
  bereichDesc: {
    color: cockpitTokens.color.textMuted,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(1.5),
  },
  ownerRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  ownerRole: {
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: cockpitTokens.color.textFaint,
  },
  ownerName: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: cockpitTokens.color.text,
  },
  mission: {
    color: cockpitTokens.color.textMuted,
    fontSize: '0.9rem',
  },
  spacer: {
    // Fixed gap between the mission text and the "Details" toggle. Not a
    // growing flex spacer: cards size to their content, so there is no leftover
    // space to push the toggle into.
    height: theme.spacing(2),
  },
  moreRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    fontSize: '0.82rem',
    fontWeight: 700,
    color: cockpitTokens.color.brand,
    paddingBottom: theme.spacing(2.5),
  },
  moreIcon: {
    fontSize: '1.1rem',
    transition: 'transform 0.2s ease',
  },
  moreIconOpen: {
    transform: 'rotate(180deg)',
  },
  body: {
    padding: theme.spacing(0, 3, 3),
  },
  section: {
    borderTop: `1px solid ${cockpitTokens.color.border}`,
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: '0.66rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: cockpitTokens.color.textFaint,
    marginBottom: theme.spacing(1),
  },
  scopeBox: {
    borderRadius: 10,
    overflow: 'hidden',
    border: `1px solid ${cockpitTokens.color.border}`,
  },
  scopeRow: {
    padding: theme.spacing(1.4, 1.8),
    fontSize: '0.89rem',
  },
  scopeIn: {
    background: 'rgba(98, 13, 255, 0.14)',
    borderBottom: `1px solid ${cockpitTokens.color.border}`,
    color: cockpitTokens.color.textMuted,
  },
  scopeOut: {
    background: 'rgba(255, 255, 255, 0.03)',
    color: cockpitTokens.color.textFaint,
  },
  scopeLabel: {
    display: 'block',
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  scopeLabelIn: {
    color: cockpitTokens.color.brand,
  },
  scopeLabelOut: {
    color: cockpitTokens.color.textFaint,
  },
  questionList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  questionItem: {
    position: 'relative',
    padding: theme.spacing(0.5, 0, 0.5, 2.2),
    color: cockpitTokens.color.textMuted,
    fontSize: '0.89rem',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '0.75em',
      width: 6,
      height: 6,
      borderRadius: '50%',
      background: 'var(--field-color)',
    },
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  tag: {
    background: 'rgba(255, 255, 255, 0.06)',
    border: `1px solid ${cockpitTokens.color.border}`,
    borderRadius: 7,
    padding: theme.spacing(0.5, 1.2),
    fontSize: '0.82rem',
    color: cockpitTokens.color.textMuted,
  },
  why: {
    background: 'rgba(0, 0, 0, 0.35)',
    borderRadius: 12,
    padding: theme.spacing(1.8, 2),
    color: cockpitTokens.color.textMuted,
    fontSize: '0.89rem',
  },
  whyLabel: {
    display: 'block',
    color: cockpitTokens.color.brand,
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(0.9),
  },
  backupRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.9),
  },
  backupButton: {
    font: 'inherit',
    fontSize: '0.85rem',
    fontWeight: 600,
    background: 'none',
    border: `1px solid ${cockpitTokens.color.borderHover}`,
    color: cockpitTokens.color.text,
    borderRadius: cockpitTokens.radius.pill,
    padding: theme.spacing(0.7, 1.6),
    cursor: 'pointer',
    '&:hover': {
      background: 'rgba(98, 13, 255, 0.25)',
    },
    '&:focus-visible': {
      outline: `2px solid ${cockpitTokens.color.brand}`,
      outlineOffset: 2,
    },
  },
  footNote: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${cockpitTokens.color.border}`,
    color: cockpitTokens.color.textFaint,
    fontSize: '0.82rem',
  },
}));

/** One area card: collapsed shows the summary, expanded the full detail. */
const AreaCard = ({
  area,
  open,
  flashing,
  onToggle,
  onJumpToOwner,
}: {
  area: ResponsibilityArea;
  open: boolean;
  flashing: boolean;
  onToggle: () => void;
  onJumpToOwner: (ownerName: string) => void;
}) => {
  const classes = useStyles();
  const anchor = areaAnchorId(area.owner);
  const bodyId = `${anchor}-body`;

  return (
    <article
      id={anchor}
      className={`${classes.card}${flashing ? ` ${classes.cardFlash}` : ''}`}
      // Consumed by the card's own rules and by nested elements (badge,
      // question bullets) via var(--field-color).
      style={
        {
          '--field-color': RESPONSIBILITY_FIELDS[area.field].color,
          scrollMarginTop: 24,
        } as React.CSSProperties
      }
    >
      <button
        type="button"
        className={classes.head}
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <span className={classes.fieldBadge}>{area.field}</span>
        <Typography component="h3" className={classes.bereich}>
          {area.bereich}
        </Typography>
        <Typography className={classes.bereichDesc}>
          {area.bereichDesc}
        </Typography>
        <span className={classes.ownerRow}>
          <span className={classes.ownerRole}>Verantwortlich</span>
          <span className={classes.ownerName}>{area.owner}</span>
        </span>
        <Typography className={classes.mission}>{area.mission}</Typography>
        <span className={classes.spacer} />
        <span className={classes.moreRow}>
          Details
          <ExpandMoreIcon
            className={`${classes.moreIcon}${
              open ? ` ${classes.moreIconOpen}` : ''
            }`}
          />
        </span>
      </button>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <div className={classes.body} id={bodyId}>
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>Scope</Typography>
            <div className={classes.scopeBox}>
              <div className={`${classes.scopeRow} ${classes.scopeIn}`}>
                <span
                  className={`${classes.scopeLabel} ${classes.scopeLabelIn}`}
                >
                  Gehört dazu
                </span>
                {area.scopeIn}
              </div>
              <div className={`${classes.scopeRow} ${classes.scopeOut}`}>
                <span
                  className={`${classes.scopeLabel} ${classes.scopeLabelOut}`}
                >
                  Gehört nicht dazu
                </span>
                {area.scopeOut}
              </div>
            </div>
          </div>

          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>Leitfragen</Typography>
            <ul className={classes.questionList}>
              {area.questions.map(question => (
                <li key={question} className={classes.questionItem}>
                  {question}
                </li>
              ))}
            </ul>
          </div>

          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Benötigte Skills im Bereich
            </Typography>
            <div className={classes.tags}>
              {area.skills.map(skill => (
                <span key={skill} className={classes.tag}>
                  {skill}
                </span>
              ))}
            </div>
          </div>

          <div className={classes.section}>
            <p className={classes.why}>
              <span className={classes.whyLabel}>
                Skills von {firstName(area.owner)}
              </span>
              {area.why}
            </p>
          </div>

          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>Backup</Typography>
            <div className={classes.backupRow}>
              {area.backup.map(person => (
                <button
                  key={person}
                  type="button"
                  className={classes.backupButton}
                  onClick={() => onJumpToOwner(person)}
                >
                  {person} →
                </button>
              ))}
            </div>
          </div>
        </div>
      </Collapse>
    </article>
  );
};

/**
 * Verantwortungslandkarte (DX-180) — who owns which area of responsibility.
 *
 * Filter by field, expand a card for the full detail, and jump from a backup
 * to that person's own area. Content lives in `responsibilityAreas.ts`.
 *
 * **Interim solution**, deliberately self-contained: one route, one data
 * module, no catalog or backend dependencies. The layout follows the Figma
 * board so the familiar view survives the move into the portal. Expect this to
 * be rebuilt — likely on top of catalog ownership — so prefer extending the
 * data module over growing this component.
 */
export const ResponsibilityMapPage = () => {
  const classes = useStyles();
  const [filter, setFilter] = useState<FieldFilter>('Alle');
  const [openOwners, setOpenOwners] = useState<ReadonlySet<string>>(new Set());
  const [flashingOwner, setFlashingOwner] = useState<string | undefined>();

  /** Clear the highlight after it has done its job of drawing the eye. */
  useEffect(() => {
    if (!flashingOwner) {
      return undefined;
    }
    const timer = window.setTimeout(() => setFlashingOwner(undefined), 1600);
    return () => window.clearTimeout(timer);
  }, [flashingOwner]);

  const visible = useMemo(
    () =>
      filter === 'Alle'
        ? responsibilityAreas
        : responsibilityAreas.filter(area => area.field === filter),
    [filter],
  );

  const allVisibleOpen =
    visible.length > 0 && visible.every(area => openOwners.has(area.owner));

  const toggleOwner = useCallback((owner: string) => {
    setOpenOwners(previous => {
      const next = new Set(previous);
      if (!next.delete(owner)) {
        next.add(owner);
      }
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setOpenOwners(previous => {
      const next = new Set(previous);
      if (allVisibleOpen) {
        visible.forEach(area => next.delete(area.owner));
      } else {
        visible.forEach(area => next.add(area.owner));
      }
      return next;
    });
  }, [allVisibleOpen, visible]);

  /**
   * Jump to the area owned by `ownerName`. The target may be filtered out, so
   * clear the filter first and scroll after the browser has painted the now-
   * visible card — otherwise `scrollIntoView` targets a hidden element.
   */
  const jumpToOwner = useCallback((ownerName: string) => {
    const target = responsibilityAreas.find(area => area.owner === ownerName);
    if (!target) {
      return;
    }
    setFilter('Alle');
    setOpenOwners(previous => new Set(previous).add(ownerName));
    setFlashingOwner(ownerName);
    requestAnimationFrame(() => {
      document
        .getElementById(areaAnchorId(ownerName))
        ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }, []);

  /**
   * Open and reveal the area a `#…` deep link points at — the home page's
   * Teams tab links to `/verantwortung#verantwortung-<owner>`, and without this
   * the card would arrive collapsed with the browser unable to reach an anchor
   * inside a not-yet-rendered `Collapse`.
   */
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) {
      return;
    }
    const target = responsibilityAreas.find(
      area => areaAnchorId(area.owner) === hash,
    );
    if (!target) {
      return;
    }
    setOpenOwners(new Set([target.owner]));
    setFlashingOwner(target.owner);
    requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ block: 'start' });
    });
  }, []);

  const chips: FieldFilter[] = ['Alle', ...RESPONSIBILITY_FIELD_ORDER];
  const countFor = (value: FieldFilter) =>
    value === 'Alle'
      ? responsibilityAreas.length
      : responsibilityAreas.filter(area => area.field === value).length;

  return (
    <Page themeId="home">
      <Header title="Verantwortungslandkarte" subtitle="Wer verantwortet was">
        <HeaderLabel label="Stand" value={RESPONSIBILITY_MAP_AS_OF} />
        <HeaderLabel
          label="Bereiche"
          value={String(responsibilityAreas.length)}
        />
        <HeaderLabel
          label="Felder"
          value={String(RESPONSIBILITY_FIELD_ORDER.length)}
        />
      </Header>
      <Content>
        <Typography className={classes.intro}>
          Wer verantwortet welchen Bereich — inklusive der bewusst gesetzten
          Abgrenzung, was <em>nicht</em> dazugehört. Gedacht für die Frage „wen
          frage ich dazu?" und für das Onboarding. Jede Karte lässt sich für
          Scope, Leitfragen, benötigte Skills und Backup aufklappen.
        </Typography>

        <Box className={classes.toolbar}>
          {chips.map(value => (
            <Chip
              key={value}
              label={
                <>
                  {value}
                  {/* aria-hidden: the accessible name below already states the
                      count in words, so screen readers skip the bare digit. */}
                  <span className={classes.chipCount} aria-hidden="true">
                    ({countFor(value)})
                  </span>
                </>
              }
              aria-label={`${value}, ${countFor(value)} Bereiche`}
              size="small"
              clickable
              onClick={() => setFilter(value)}
              className={`${classes.chip}${
                filter === value ? ` ${classes.chipActive}` : ''
              }`}
              aria-pressed={filter === value}
            />
          ))}
          <button
            type="button"
            className={classes.expandAll}
            onClick={toggleAllVisible}
          >
            {allVisibleOpen ? 'Alle zuklappen' : 'Alle aufklappen'}
          </button>
        </Box>

        <Typography className={classes.count} aria-live="polite">
          {visible.length === responsibilityAreas.length
            ? `${responsibilityAreas.length} Verantwortungsbereiche`
            : `${visible.length} von ${responsibilityAreas.length} Verantwortungsbereichen`}
        </Typography>

        <Grid container spacing={3} alignItems="flex-start">
          {visible.map(area => (
            <Grid item xs={12} md={6} key={area.owner}>
              <AreaCard
                area={area}
                open={openOwners.has(area.owner)}
                flashing={flashingOwner === area.owner}
                onToggle={() => toggleOwner(area.owner)}
                onJumpToOwner={jumpToOwner}
              />
            </Grid>
          ))}
        </Grid>

        <Typography className={classes.footNote}>
          Vertraulich · intern · Organisation 2026 · Stand{' '}
          {RESPONSIBILITY_MAP_AS_OF} ·{' '}
          <Link to={RESPONSIBILITY_MAP_SOURCE_URL}>Quell-Board in Figma →</Link>
        </Typography>
      </Content>
    </Page>
  );
};
